import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Plus, Edit2, Trash2, X, Check, Package, ShoppingBag, Users, MessageSquare, Upload, Eye, EyeOff } from "lucide-react";
import axios from "axios";
import { useLanguage } from "../contexts/LanguageContext";

const BASE_URL = (process.env.REACT_APP_BACKEND_URL || "https://lirkprint-backend.onrender.com").replace(/\/$/, "");
const API = `${BASE_URL}/api`;

const STATUS_OPTS = ["draft", "published", "featured"];
const CATEGORY_OPTS = ["sculpture", "geometric", "architecture", "functional", "general"];
const ORDER_STATUS_OPTS = ["pending", "processing", "shipped", "delivered", "cancelled"];

const emptyProduct = {
  name_de: "", name_en: "",
  description_de: "", description_en: "",
  price: "", category: "sculpture",
  in_showroom: true, in_shop: true,
  status: "published", stock: 1,
  sort_order: 0, material: "",
  dimensions: "", images: [""]
};

function FormField({ label, name, type = "text", placeholder, value, onChange }) {
  return (
    <div>
      <label className="block text-[10px] font-mono tracking-widest uppercase text-foreground/40 mb-1.5">{label}</label>
      <input
        data-testid={`admin-field-${name}`}
        type={type}
        value={value || ""}
        onChange={e => onChange(name, e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2.5 bg-background border border-border rounded-sm text-foreground text-sm focus:outline-none focus:border-foreground/40 transition-colors placeholder:text-foreground/20"
      />
    </div>
  );
}

function FormTextarea({ label, name, value, onChange }) {
  return (
    <div>
      <label className="block text-[10px] font-mono tracking-widest uppercase text-foreground/40 mb-1.5">{label}</label>
      <textarea
        data-testid={`admin-field-${name}`}
        rows={3}
        value={value || ""}
        onChange={e => onChange(name, e.target.value)}
        className="w-full px-3 py-2.5 bg-background border border-border rounded-sm text-foreground text-sm focus:outline-none focus:border-foreground/40 transition-colors resize-none"
      />
    </div>
  );
}

export default function Admin() {
  const { lang } = useLanguage();
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState({});
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [formData, setFormData] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    if (tab === "stats") fetchStats();
    else if (tab === "products") fetchProducts();
    else if (tab === "orders") fetchOrders();
    else if (tab === "messages") fetchMessages();
  }, [tab]); // eslint-disable-line

  const fetchStats = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(data);
    } finally { setLoading(false); }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/products`, { withCredentials: true });
      setProducts(data.products || []);
    } finally { setLoading(false); }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/orders`, { withCredentials: true });
      setOrders(data.orders || []);
    } finally { setLoading(false); }
  };

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/contact`, { withCredentials: true });
      setMessages(data.messages || []);
    } finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditProduct(null);
    setFormData(emptyProduct);
    setShowForm(true);
  };

  const openEdit = (product) => {
    setEditProduct(product);
    setFormData({
      ...product,
      images: product.images?.length ? product.images : [""],
      price: product.price || ""
    });
    setShowForm(true);
  };

  const saveProduct = async () => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        stock: parseInt(formData.stock) || 0,
        sort_order: parseInt(formData.sort_order) || 0,
        images: formData.images.filter(Boolean)
      };
      if (editProduct) {
        await axios.put(`${API}/admin/products/${editProduct.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/products`, payload, { withCredentials: true });
      }
      setShowForm(false);
      fetchProducts();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async (id) => {
    await axios.delete(`${API}/admin/products/${id}`, { withCredentials: true });
    setDeleteConfirm(null);
    fetchProducts();
  };

  const updateOrderStatus = async (id, status) => {
    await axios.put(`${API}/admin/orders/${id}/status`, { status }, { withCredentials: true });
    fetchOrders();
  };

  const markMessageRead = async (id) => {
    await axios.put(`${API}/admin/contact/${id}/read`, {}, { withCredentials: true });
    fetchMessages();
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const imgUrl = `${backendUrl}${data.url}`;
      setFormData(f => ({ ...f, images: [imgUrl, ...f.images.filter(Boolean)] }));
    } catch (e) {
      console.error(e);
    } finally {
      setUploadingImg(false);
    }
  };

  const handleFieldChange = (name, val) => {
    setFormData(f => ({ ...f, [name]: val }));
  };

  const tabs = [
    { key: "stats", label: "Dashboard", icon: Package },
    { key: "products", label: lang === "de" ? "Produkte" : "Products", icon: ShoppingBag },
    { key: "orders", label: lang === "de" ? "Bestellungen" : "Orders", icon: Users },
    { key: "messages", label: lang === "de" ? "Nachrichten" : "Messages", icon: MessageSquare },
  ];

  return (
    <div className="min-h-screen bg-background pt-28">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-heading font-bold tracking-tighter text-foreground">Admin</h1>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border mb-8 overflow-x-auto">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-5 py-4 text-xs font-mono tracking-widest uppercase whitespace-nowrap transition-all ${
                tab === key
                  ? "text-foreground border-b-2 border-foreground -mb-px"
                  : "text-foreground/40 hover:text-foreground"
              }`}
            >
              <Icon size={14} strokeWidth={1.5} /> {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-24">
            <div className="w-8 h-8 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Stats */}
            {tab === "stats" && (
              <div data-testid="admin-stats">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  {[
                    { label: lang === "de" ? "Produkte" : "Products", value: stats.total_products, icon: ShoppingBag },
                    { label: lang === "de" ? "Bestellungen" : "Orders", value: stats.total_orders, icon: Package },
                    { label: lang === "de" ? "Kunden" : "Customers", value: stats.total_users, icon: Users },
                    { label: lang === "de" ? "Umsatz" : "Revenue", value: `€${stats.total_revenue?.toFixed(2) || "0.00"}`, icon: Package },
                  ].map((stat, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.08 }}
                      className="p-6 bg-surface border border-border rounded-sm"
                    >
                      <stat.icon size={18} strokeWidth={1.5} className="text-foreground/30 mb-3" />
                      <p className="text-2xl font-heading font-bold text-foreground">{stat.value}</p>
                      <p className="text-xs font-mono text-foreground/40 mt-1 tracking-wider uppercase">{stat.label}</p>
                    </motion.div>
                  ))}
                </div>
                {stats.unread_messages > 0 && (
                  <div className="p-4 bg-surface border border-border rounded-sm">
                    <p className="text-sm text-foreground/60">
                      <span className="text-foreground font-mono">{stats.unread_messages}</span>{" "}
                      {lang === "de" ? "ungelesene Nachrichten" : "unread messages"}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Products */}
            {tab === "products" && (
              <div data-testid="admin-products">
                <div className="flex justify-between items-center mb-6">
                  <p className="text-xs font-mono text-foreground/40">{products.length} {lang === "de" ? "Produkte" : "products"}</p>
                  <button
                    data-testid="add-product-btn"
                    onClick={openCreate}
                    className="flex items-center gap-2 px-5 py-3 bg-foreground text-background text-xs font-mono tracking-widest uppercase rounded-sm hover:bg-foreground/90 transition-all"
                  >
                    <Plus size={14} strokeWidth={1.5} /> {lang === "de" ? "Produkt hinzufügen" : "Add Product"}
                  </button>
                </div>

                <div className="space-y-2">
                  {products.map((p, i) => (
                    <motion.div
                      key={p.id}
                      data-testid={`admin-product-row-${p.id}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.03 }}
                      className="flex items-center gap-4 p-4 bg-surface border border-border rounded-sm hover:border-foreground/20 transition-colors"
                    >
                      <div className="w-12 h-12 rounded-sm overflow-hidden bg-background flex-shrink-0">
                        <img src={p.images?.[0] || ""} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{lang === "de" ? p.name_de : p.name_en}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs font-mono text-foreground/40">€{p.price}</span>
                          <span className="text-xs font-mono text-foreground/40">{p.category}</span>
                          <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm uppercase ${
                            p.status === "featured" ? "bg-foreground text-background" :
                            p.status === "published" ? "bg-green-500/20 text-green-400" :
                            "bg-foreground/10 text-foreground/40"
                          }`}>{p.status}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="flex items-center gap-1 text-[10px] font-mono text-foreground/30">
                          {p.in_showroom && <span className="px-1.5 py-0.5 border border-border rounded-sm">S</span>}
                          {p.in_shop && <span className="px-1.5 py-0.5 border border-border rounded-sm">SH</span>}
                        </div>
                        <button
                          data-testid={`edit-product-${p.id}`}
                          onClick={() => openEdit(p)}
                          className="p-2 text-foreground/40 hover:text-foreground transition-colors"
                        >
                          <Edit2 size={14} strokeWidth={1.5} />
                        </button>
                        <button
                          data-testid={`delete-product-${p.id}`}
                          onClick={() => setDeleteConfirm(p.id)}
                          className="p-2 text-foreground/40 hover:text-destructive transition-colors"
                        >
                          <Trash2 size={14} strokeWidth={1.5} />
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Orders */}
            {tab === "orders" && (
              <div data-testid="admin-orders" className="space-y-3">
                {orders.length === 0 ? (
                  <p className="text-center py-16 text-foreground/30 font-mono">{lang === "de" ? "Keine Bestellungen" : "No orders"}</p>
                ) : orders.map((order, i) => (
                  <motion.div
                    key={order.id}
                    data-testid={`admin-order-${order.id}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.04 }}
                    className="p-5 bg-surface border border-border rounded-sm"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-mono text-foreground/30">#{order.id?.slice(-8).toUpperCase()}</p>
                        <p className="text-sm text-foreground mt-0.5">{order.user_email}</p>
                        <p className="text-xs text-foreground/40">{new Date(order.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-mono text-foreground">€{order.total?.toFixed(2)}</span>
                        <select
                          value={order.status}
                          onChange={e => updateOrderStatus(order.id, e.target.value)}
                          className="bg-background border border-border text-foreground text-xs px-2 py-2 rounded-sm focus:outline-none"
                        >
                          {ORDER_STATUS_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Messages */}
            {tab === "messages" && (
              <div data-testid="admin-messages" className="space-y-3">
                {messages.length === 0 ? (
                  <p className="text-center py-16 text-foreground/30 font-mono">{lang === "de" ? "Keine Nachrichten" : "No messages"}</p>
                ) : messages.map((msg, i) => (
                  <motion.div
                    key={msg.id}
                    data-testid={`admin-message-${msg.id}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.04 }}
                    className={`p-5 bg-surface border rounded-sm transition-colors ${msg.read ? "border-border opacity-60" : "border-foreground/30"}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="text-sm font-medium text-foreground">{msg.name}</p>
                        <p className="text-xs font-mono text-foreground/40">{msg.email} · {msg.subject}</p>
                        <p className="text-xs text-foreground/30 mt-0.5">{new Date(msg.created_at).toLocaleDateString()}</p>
                      </div>
                      {!msg.read && (
                        <button
                          data-testid={`mark-read-${msg.id}`}
                          onClick={() => markMessageRead(msg.id)}
                          className="flex items-center gap-1 text-xs font-mono text-foreground/40 hover:text-foreground transition-colors"
                        >
                          <Check size={12} strokeWidth={1.5} /> {lang === "de" ? "Gelesen" : "Mark read"}
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-foreground/60 leading-relaxed">{msg.message}</p>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Delete Confirm Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-surface border border-border rounded-sm p-8 max-w-sm w-full mx-4"
            >
              <h3 className="text-lg font-heading font-bold text-foreground mb-4">
                {lang === "de" ? "Produkt löschen?" : "Delete product?"}
              </h3>
              <p className="text-sm text-foreground/50 mb-6">
                {lang === "de" ? "Diese Aktion kann nicht rückgängig gemacht werden." : "This action cannot be undone."}
              </p>
              <div className="flex gap-3">
                <button
                  data-testid="confirm-delete-btn"
                  onClick={() => deleteProduct(deleteConfirm)}
                  className="flex-1 py-3 bg-destructive text-white text-xs font-mono tracking-widest uppercase rounded-sm hover:opacity-90"
                >
                  {lang === "de" ? "Löschen" : "Delete"}
                </button>
                <button
                  data-testid="cancel-delete-btn"
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 py-3 border border-border text-foreground/60 text-xs font-mono tracking-widest uppercase rounded-sm hover:text-foreground"
                >
                  {lang === "de" ? "Abbrechen" : "Cancel"}
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Product Form Modal */}
        {showForm && (
          <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-background/90 backdrop-blur-sm py-8 px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-surface border border-border rounded-sm w-full max-w-2xl"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                <h2 className="text-sm font-heading font-semibold text-foreground">
                  {editProduct ? (lang === "de" ? "Produkt bearbeiten" : "Edit Product") : (lang === "de" ? "Neues Produkt" : "New Product")}
                </h2>
                <button onClick={() => setShowForm(false)} className="text-foreground/40 hover:text-foreground transition-colors">
                  <X size={18} strokeWidth={1.5} />
                </button>
              </div>

              <div className="p-6 space-y-5">
                {/* Names */}
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Name DE" name="name_de" value={formData.name_de} onChange={handleFieldChange} />
                  <FormField label="Name EN" name="name_en" value={formData.name_en} onChange={handleFieldChange} />
                </div>
                {/* Descriptions */}
                <div className="grid grid-cols-2 gap-4">
                  <FormTextarea label="Beschreibung DE" name="description_de" value={formData.description_de} onChange={handleFieldChange} />
                  <FormTextarea label="Description EN" name="description_en" value={formData.description_en} onChange={handleFieldChange} />
                </div>
                {/* Price, Stock, Sort */}
                <div className="grid grid-cols-3 gap-4">
                  <FormField label="Preis (€)" name="price" type="number" value={formData.price} onChange={handleFieldChange} />
                  <FormField label="Lagerbestand" name="stock" type="number" value={formData.stock} onChange={handleFieldChange} />
                  <FormField label="Reihenfolge" name="sort_order" type="number" value={formData.sort_order} onChange={handleFieldChange} />
                </div>
                {/* Category & Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-mono tracking-widest uppercase text-foreground/40 mb-1.5">Kategorie</label>
                    <select
                      data-testid="admin-field-category"
                      value={formData.category}
                      onChange={e => setFormData(f => ({ ...f, category: e.target.value }))}
                      className="w-full px-3 py-2.5 bg-background border border-border rounded-sm text-foreground text-sm focus:outline-none"
                    >
                      {CATEGORY_OPTS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-mono tracking-widest uppercase text-foreground/40 mb-1.5">Status</label>
                    <select
                      data-testid="admin-field-status"
                      value={formData.status}
                      onChange={e => setFormData(f => ({ ...f, status: e.target.value }))}
                      className="w-full px-3 py-2.5 bg-background border border-border rounded-sm text-foreground text-sm focus:outline-none"
                    >
                      {STATUS_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                {/* Material & Dimensions */}
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Material" name="material" value={formData.material} onChange={handleFieldChange} />
                  <FormField label="Abmessungen" name="dimensions" value={formData.dimensions} onChange={handleFieldChange} />
                </div>
                {/* Visibility */}
                <div className="flex gap-6">
                  {[
                    { key: "in_showroom", label: "Im Showroom" },
                    { key: "in_shop", label: "Im Shop" }
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={!!formData[key]}
                        onChange={e => setFormData(f => ({ ...f, [key]: e.target.checked }))}
                        className="sr-only"
                      />
                      <div
                        className={`w-8 h-4 rounded-full transition-colors ${formData[key] ? "bg-foreground" : "bg-foreground/20"}`}
                      >
                        <div className={`w-3 h-3 bg-background rounded-full transition-transform mt-0.5 mx-0.5 ${formData[key] ? "translate-x-4" : "translate-x-0"}`} />
                      </div>
                      <span className="text-xs font-mono text-foreground/60">{label}</span>
                    </label>
                  ))}
                </div>
                {/* Images */}
                <div>
                  <label className="block text-[10px] font-mono tracking-widest uppercase text-foreground/40 mb-2">Bilder</label>
                  <div className="space-y-2 mb-3">
                    {(formData.images || [""]).map((url, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={url}
                          onChange={e => {
                            const imgs = [...(formData.images || [""])];
                            imgs[i] = e.target.value;
                            setFormData(f => ({ ...f, images: imgs }));
                          }}
                          placeholder="https://... oder nach Upload"
                          className="flex-1 px-3 py-2 bg-background border border-border rounded-sm text-foreground text-sm focus:outline-none focus:border-foreground/40 transition-colors placeholder:text-foreground/20"
                        />
                        {url && <img src={url} alt="" className="w-10 h-10 rounded-sm object-cover border border-border flex-shrink-0" />}
                        {(formData.images || []).length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const imgs = (formData.images || []).filter((_, idx) => idx !== i);
                              setFormData(f => ({ ...f, images: imgs.length ? imgs : [""] }));
                            }}
                            className="p-2 text-foreground/40 hover:text-destructive transition-colors flex-shrink-0"
                            title="Bild entfernen"
                          >
                            <Trash2 size={14} strokeWidth={1.5} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-3">
                    <label className="flex items-center gap-2 px-4 py-2 border border-border rounded-sm text-xs font-mono text-foreground/60 hover:text-foreground hover:border-foreground/40 cursor-pointer transition-colors">
                      <Upload size={12} strokeWidth={1.5} />
                      {uploadingImg ? (lang === "de" ? "Lädt..." : "Uploading...") : (lang === "de" ? "Bild hochladen" : "Upload image")}
                      <input type="file" accept="image/*" className="sr-only" onChange={handleImageUpload} />
                    </label>
                    <button
                      type="button"
                      onClick={() => setFormData(f => ({ ...f, images: [...(f.images || []), ""] }))}
                      className="px-4 py-2 border border-border rounded-sm text-xs font-mono text-foreground/60 hover:text-foreground hover:border-foreground/40 transition-colors"
                    >
                      + URL hinzufügen
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 px-6 py-4 border-t border-border">
                <button
                  data-testid="save-product-btn"
                  onClick={saveProduct}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-3 bg-foreground text-background text-xs font-mono tracking-widest uppercase rounded-sm hover:bg-foreground/90 transition-all disabled:opacity-40"
                >
                  <Check size={14} strokeWidth={1.5} />
                  {saving ? (lang === "de" ? "Speichern..." : "Saving...") : (lang === "de" ? "Speichern" : "Save")}
                </button>
                <button
                  onClick={() => setShowForm(false)}
                  className="px-6 py-3 border border-border text-foreground/60 text-xs font-mono tracking-widest uppercase rounded-sm hover:text-foreground transition-all"
                >
                  {lang === "de" ? "Abbrechen" : "Cancel"}
                </button>
              </div>
            </motion.div>
          </div>
        )}
        <div className="pb-24" />
      </div>
    </div>
  );
}
